import { Button } from "@/core/components/ui/button";
import { DialogFooter } from "@/core/components/ui/dialog";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import type { ApInvoiceItem } from "@/accounts-payable/types";
import {
  formatDollars,
  hasPricebookMatch,
  matchedPricebookItemLabel,
} from "@/accounts-payable/utils";

/**
 * The first step of a pick reached from a proposal about several lines
 * invoiced under one SKU. They are usually the same part, but a vendor's SKU
 * can name a product family with the size in the description, so the person
 * says which line the pick is for (the next step offers the rest).
 */
export const ProposalLineChooser = ({
  lines,
  onChoose,
  onClose,
}: {
  lines: ApInvoiceItem[];
  onChoose: (line: ApInvoiceItem) => void;
  onClose: () => void;
}) => (
  <>
    <Stack gap="sm" fullWidth className="px-6">
      <Typography variant="bodyW2" textColor="secondary">
        This proposal is about {lines.length} lines invoiced as {lines[0].sku}.
        Which one are you matching?
      </Typography>
      <Stack gap="none" fullWidth role="list">
        {lines.map((line) => (
          <Stack key={line.id} role="listitem" fullWidth>
            <button
              type="button"
              onClick={() => onChoose(line)}
              className="w-full rounded-md px-3 py-2 text-left hover:bg-blue-5 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
            >
              <Stack gap="none">
                <Typography variant="bodyW2">
                  {line.quantity} × {line.description}
                </Typography>
                <Typography variant="captionW1" textColor="tertiary">
                  {formatDollars(line.unit_cost)}
                  {hasPricebookMatch(line) &&
                    ` · now ${matchedPricebookItemLabel(line)}`}
                </Typography>
              </Stack>
            </button>
          </Stack>
        ))}
      </Stack>
    </Stack>
    <DialogFooter className="flex-row justify-end sm:space-x-0">
      <Button variant="secondary" size="sm" onClick={onClose}>
        Cancel
      </Button>
    </DialogFooter>
  </>
);
